import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, MapPin } from "lucide-react";
import { HERO, HERO_PILLS, IMAGES, LINKS } from "../data/site";

const lineTransition = (i) => ({
  duration: 0.9,
  delay: 0.15 + i * 0.14,
  ease: [0.22, 1, 0.36, 1],
});

export const Hero = () => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-24, 24]);

  return (
    <section id="hero" data-testid="hero-section" className="relative overflow-hidden pb-24 pt-32 sm:pt-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-7 inline-flex items-center gap-2 rounded-full border border-line bg-white px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.18em] text-burgundy"
              data-testid="hero-eyebrow"
            >
              <MapPin className="h-[13px] w-[13px]" />
              {HERO.eyebrow}
            </motion.p>

            <h1
              data-testid="hero-heading"
              className="font-display text-[40px] font-bold leading-[44px] tracking-[-1.5px] text-ink sm:text-[60px] sm:leading-[60px]"
            >
              {HERO.lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${line.accent ? "italic text-burgundy" : ""}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={lineTransition(i)}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-soft sm:text-lg"
              data-testid="hero-subtitle"
            >
              {HERO.lead}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                href="#valasztek"
                data-testid="hero-cta-catalog"
                className="btn-pill bg-night text-cream hover:scale-[1.04]"
              >
                Választék megtekintése
                <ArrowDown className="h-4 w-4" />
              </a>
              <a
                href={LINKS.mainSite}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-webshop"
                className="btn-pill border border-ink/20 bg-white text-ink transition-colors duration-200 hover:border-burgundy hover:text-burgundy"
              >
                comfortbutor.hu
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 1.1 }}
              className="mt-8 flex max-w-lg flex-wrap gap-2.5"
              data-testid="hero-pills"
            >
              {HERO_PILLS.map((p) => (
                <span
                  key={p}
                  data-testid={`hero-pill-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="rounded-full border border-line bg-white px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-soft"
                >
                  {p}
                </span>
              ))}
            </motion.div>
          </div>

          <motion.div
            ref={imgRef}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-3xl card-shadow-lg" data-testid="hero-image">
              <motion.img
                src={IMAGES.hero}
                alt={IMAGES.heroAlt}
                width="2000"
                height="1408"
                loading="eager"
                fetchpriority="high"
                className="block h-auto w-full scale-[1.06]"
                style={{ y }}
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="absolute -bottom-6 left-5 rounded-2xl bg-white px-6 py-4 card-shadow-lg sm:left-8"
              data-testid="hero-badge"
            >
              <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-burgundy">
                {HERO.badgeEyebrow}
              </p>
              <p className="mt-1 font-display text-base font-semibold text-ink">{HERO.badgeLine}</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
